import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, Truck, Save } from "lucide-react";
import { toast } from "sonner";

const CFG_FIELDS = [
  { k: "nolo_fier_pct", label: "Nolo FIER", suffix: "(frazione, es. 0.055 = 5,5%)" },
  { k: "fuel_pct", label: "Fuel surcharge", suffix: "(frazione, es. 0.08 = 8%)" },
  { k: "diritto_fisso", label: "Diritto fisso", suffix: "€" },
  { k: "trasmissione_dati", label: "Trasmissione dati", suffix: "€" },
  { k: "assicurazione_permille", label: "Assicurazione", suffix: "(per mille, es. 0.005)" },
  { k: "assicurazione_min", label: "Assicurazione minima", suffix: "€" },
  { k: "preavviso", label: "Preavviso telefonico", suffix: "€ (sempre incluso)" },
  { k: "orario_pct", label: "Fascia oraria ristretta", suffix: "(frazione, es. 0.15 = 15%)" },
  { k: "orario_min", label: "Fascia oraria minima", suffix: "€" },
  { k: "sponda", label: "Sponda idraulica", suffix: "€ (sempre inclusa)" },
  { k: "volumetric_coeff", label: "Coeff. volumetrico", suffix: "kg/m³" },
  { k: "min_tassabile_kg", label: "Peso minimo tassabile", suffix: "kg" },
  { k: "min_kg_per_collo", label: "Peso minimo per collo", suffix: "kg" },
  { k: "supp_non_sovrapponibile", label: "Suppl. non sovrapponibile", suffix: "(frazione, es. 0.40)" },
  { k: "supp_fasci", label: "Suppl. fasci", suffix: "(frazione, es. 0.50)" },
  { k: "supp_adr", label: "Suppl. ADR", suffix: "(frazione, es. 0.05)" },
  { k: "moduli_per_pallet", label: "Moduli per pallet", suffix: "pz" },
  { k: "volume_per_pallet_m3", label: "Volume per pallet", suffix: "m³" },
];

const SCAGLIONI = ["1-50", "51-100", "101-1000", "1001-2000", "2001-3000", ">3000"];

export default function ConfigTrasporto() {
  const { user } = useAuth();
  const allowed = ["admin", "commercial_manager"].includes(user?.role);
  const [cfg, setCfg] = useState(null);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingCfg, setSavingCfg] = useState(false);
  const [savingZone, setSavingZone] = useState(null);

  useEffect(() => {
    if (!allowed) { setLoading(false); return; }
    Promise.all([api.get("/transport/config"), api.get("/transport/zones")])
      .then(([c, z]) => { setCfg(c.data); setZones(z.data); })
      .catch(() => toast.error("Errore caricamento configurazione"))
      .finally(() => setLoading(false));
  }, [allowed]);

  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      const body = {};
      CFG_FIELDS.forEach((f) => { body[f.k] = +cfg[f.k] || 0; });
      body.aumento_listino_years = (cfg.aumento_listino_years || []).map((y) => ({ anno: +y.anno || 0, pct: +y.pct || 0 }));
      const r = await api.put("/transport/config", body);
      setCfg(r.data);
      toast.success("Parametri salvati");
    } catch { toast.error("Errore salvataggio parametri"); }
    finally { setSavingCfg(false); }
  };

  const years = cfg?.aumento_listino_years || [];
  const setYears = (next) => setCfg((c) => ({ ...c, aumento_listino_years: next }));
  const addYear = () => setYears([...years, { anno: years.length ? Math.max(...years.map((y) => +y.anno || 0)) + 1 : new Date().getFullYear(), pct: 0 }]);
  const setYear = (i, k, v) => setYears(years.map((y, j) => (j === i ? { ...y, [k]: v } : y)));
  const removeYear = (i) => setYears(years.filter((_, j) => j !== i));
  const totalAum = years.reduce((a, y) => a + (+y.pct || 0), 0);

  const setBracket = (zi, bi, v) => setZones((zs) => zs.map((z, i) => (i === zi ? { ...z, brackets: z.brackets.map((b, j) => (j === bi ? v : b)) } : z)));

  const saveZone = async (z) => {
    setSavingZone(z.zone);
    try {
      await api.put(`/transport/zones/${encodeURIComponent(z.zone)}`, { brackets: z.brackets.map((b) => +b || 0) });
      toast.success(`Zona ${z.label} aggiornata`);
    } catch { toast.error("Errore salvataggio zona"); }
    finally { setSavingZone(null); }
  };

  if (loading) return <div className="py-20 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;
  if (!allowed) return <div className="py-20 text-center text-muted-foreground" data-testid="config-trasporto-denied">Accesso negato. Sezione riservata agli amministratori.</div>;
  if (!cfg) return null;

  return (
    <div className="space-y-6" data-testid="config-trasporto-page">
      <div>
        <h1 className="flex items-center gap-2 font-heading text-3xl font-black"><Truck className="h-7 w-7 text-primary" /> Configurazione Trasporto</h1>
        <p className="text-sm text-muted-foreground">Parametri di calcolo e tariffario ARCO (€/100 kg) per il Preventivatore.</p>
      </div>

      <Card className="p-4" data-testid="transport-config-card">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-bold">Parametri di calcolo</h2>
          <Button size="sm" onClick={saveCfg} disabled={savingCfg} data-testid="transport-save-config">
            {savingCfg ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Salva parametri
          </Button>
        </div>
        <div className="mb-4 rounded-lg border border-primary/30 bg-primary/[0.04] p-3" data-testid="transport-aumenti-listino">
          <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
            <Label className="text-sm font-bold">Aumento listino per anno</Label>
            <span className="rounded-full border border-primary/40 bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary" data-testid="aumento-listino-totale">
              Totale applicato: {totalAum.toFixed(4)} ({(totalAum * 100).toLocaleString("it-IT", { maximumFractionDigits: 2 })}%)
            </span>
          </div>
          <p className="mb-3 text-xs text-muted-foreground">Gli aumenti annuali <b>si sommano</b> (es. 2025 0.03 + 2026 0.05 = 0.08 → 8%). Aggiungi ogni anno la voce di riferimento.</p>
          <div className="space-y-2">
            {years.map((y, i) => (
              <div key={i} className="flex flex-wrap items-end gap-2" data-testid={`aumento-row-${i}`}>
                <div className="w-24">
                  <Label className="text-[11px] text-muted-foreground">Anno</Label>
                  <Input type="number" step="1" value={y.anno ?? ""} onChange={(e) => setYear(i, "anno", e.target.value)} data-testid={`aumento-anno-${i}`} />
                </div>
                <div className="w-40">
                  <Label className="text-[11px] text-muted-foreground">Aumento (frazione)</Label>
                  <Input type="number" step="any" value={y.pct ?? ""} onChange={(e) => setYear(i, "pct", e.target.value)} data-testid={`aumento-pct-${i}`} />
                </div>
                <span className="pb-2 text-xs text-muted-foreground">= {((+y.pct || 0) * 100).toLocaleString("it-IT", { maximumFractionDigits: 2 })}%</span>
                <Button size="sm" variant="ghost" className="pb-0 text-red-500 hover:text-red-600" onClick={() => removeYear(i)} data-testid={`aumento-remove-${i}`}>Rimuovi</Button>
              </div>
            ))}
            {years.length === 0 && <div className="text-xs text-muted-foreground">Nessun aumento configurato.</div>}
          </div>
          <Button size="sm" variant="outline" className="mt-3" onClick={addYear} data-testid="aumento-add-year">+ Aggiungi anno</Button>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {CFG_FIELDS.map((f) => (
            <div key={f.k}>
              <Label className="text-xs">{f.label} <span className="text-muted-foreground">{f.suffix}</span></Label>
              <Input type="number" step="any" value={cfg[f.k] ?? ""} onChange={(e) => setCfg({ ...cfg, [f.k]: e.target.value })} data-testid={`transport-cfg-${f.k}`} />
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-0" data-testid="transport-zones-card">
        <div className="p-4 pb-2"><h2 className="text-lg font-bold">Tariffario ARCO per zona <span className="text-sm font-normal text-muted-foreground">(€/100 kg, per scaglione di peso)</span></h2></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs">
              <tr>
                <th className="p-2 text-left">Zona</th>
                {SCAGLIONI.map((s) => <th key={s} className="p-2 text-right">{s} kg</th>)}
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {zones.map((z, zi) => (
                <tr key={z.zone} className="border-t border-input" data-testid={`transport-zone-${z.zone}`}>
                  <td className="p-2">
                    <div className="font-semibold">{z.label} {z.island && <Badge variant="secondary" className="ml-1 text-[10px]">isola</Badge>}</div>
                    <div className="text-[10px] text-muted-foreground">{(z.provinces || []).join(", ")}</div>
                  </td>
                  {z.brackets.map((b, bi) => (
                    <td key={bi} className="p-1"><Input type="number" step="0.01" value={b} onChange={(e) => setBracket(zi, bi, e.target.value)} className="h-8 w-20 text-right text-xs" /></td>
                  ))}
                  <td className="p-1 text-right">
                    <Button size="sm" variant="outline" onClick={() => saveZone(z)} disabled={savingZone === z.zone} data-testid={`transport-save-zone-${z.zone}`}>
                      {savingZone === z.zone ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
